import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, Tag, Search, X, Eye } from "lucide-react";
import { BRIEFS } from "../../data";

const fmt = (d) =>
  new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });

const norm = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Word (.doc/.docx) se muestra con el visor de Office Online; el PDF lo renderiza el navegador.
const viewerUrl = (b) => {
  const isDoc = (b.format || "").toUpperCase() === "DOC" || /\.docx?($|\?)/i.test(b.pdf);
  if (isDoc) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(b.pdf)}`;
  }
  return b.pdf;
};

const PAGE = 9;

export default function PolicyBriefs() {
  const topics = useMemo(
    () => ["Todos", ...Array.from(new Set(BRIEFS.map((b) => b.topic)))],
    []
  );
  const [filter, setFilter] = useState("Todos");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const [reading, setReading] = useState(null);

  const list = useMemo(() => {
    const q = norm(query.trim());
    return BRIEFS.filter((b) => {
      const byTopic = filter === "Todos" || b.topic === filter;
      const byQuery =
        !q ||
        norm(`${b.title} ${b.topic} ${b.summary}`).includes(q);
      return byTopic && byQuery;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filter, query]);

  useEffect(() => {
    setVisible(PAGE);
  }, [filter, query]);

  const shown = list.slice(0, visible);

  return (
    <section id="briefs" className="py-20 lg:py-28 bg-white border-y border-gray-200">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-3">
              Documentos técnicos
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              Policy Briefs · Análisis
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Propuestas y documentos técnicos sobre los principales retos de España.
              Cada uno con fuentes citadas y descargable.
            </p>
          </div>

          <div className="relative w-full lg:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="brief-search"
              placeholder="Buscar por palabra clave..."
              className="w-full border border-gray-300 rounded-sm pl-10 pr-9 py-3 focus:outline-none focus:ring-1 focus:ring-espana-red focus:border-espana-red transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="brief-search-clear"
                aria-label="Limpiar búsqueda"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-espana-red transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {topics.map((t) => (
            <button
              key={t}
              data-testid={`brief-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`text-sm font-bold px-4 py-2 rounded-sm border transition-colors ${
                filter === t
                  ? "bg-espana-red text-white border-espana-red"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm text-gray-500" data-testid="brief-count">
          {list.length} {list.length === 1 ? "documento" : "documentos"}
          {filter !== "Todos" ? ` en "${filter}"` : ""}
          {query ? ` para "${query}"` : ""}
        </p>

        {list.length === 0 && (
          <p className="mt-8 text-gray-500" data-testid="brief-empty">
            No se encontraron documentos. Prueba con otra palabra clave o tema.
          </p>
        )}

        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((b, i) => (
            <motion.article
              key={b.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              data-testid={`brief-card-${b.id}`}
              className="group flex flex-col bg-white border border-gray-200 p-6 hover:-translate-y-1 hover:shadow-md transition-transform"
            >
              <div className="flex items-center gap-2 text-espana-red mb-4">
                <FileText size={20} />
                <span className="text-xs font-bold uppercase tracking-widest">
                  {b.format || "PDF"}
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 mb-2">
                <Tag size={13} /> {b.topic}
              </span>
              <h3 className="text-lg font-bold leading-snug mb-2 group-hover:text-espana-red transition-colors">
                {b.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed flex-1">{b.summary}</p>
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                  <Calendar size={13} /> {fmt(b.date)}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setReading(b)}
                    data-testid={`brief-read-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-600 hover:text-espana-red transition-colors"
                  >
                    <Eye size={16} /> Leer
                  </button>
                  <a
                    href={b.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`brief-download-${b.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-espana-red hover:text-espana-reddark transition-colors"
                  >
                    <Download size={16} /> Descargar
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {visible < list.length && (
          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              onClick={() => setVisible((v) => v + PAGE)}
              data-testid="brief-load-more"
              className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 font-bold px-7 py-3.5 rounded-sm transition-colors"
            >
              Ver más documentos
            </button>
            <span className="text-xs text-gray-400">
              Mostrando {shown.length} de {list.length}
            </span>
          </div>
        )}
      </div>

      {reading && (
        <div
          data-testid="brief-reader"
          className="fixed inset-0 z-[60] bg-black/70 flex flex-col p-4 sm:p-8"
          onClick={() => setReading(null)}
        >
          <div
            className="bg-white w-full max-w-5xl mx-auto flex-1 flex flex-col rounded-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-gray-200">
              <h3 className="font-bold text-sm sm:text-base truncate">{reading.title}</h3>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href={reading.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reader-download"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-espana-red hover:text-espana-reddark transition-colors"
                >
                  <Download size={16} /> Descargar
                </a>
                <button
                  onClick={() => setReading(null)}
                  data-testid="reader-close"
                  aria-label="Cerrar"
                  className="text-gray-500 hover:text-espana-red transition-colors"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <iframe
              title={reading.title}
              src={viewerUrl(reading)}
              className="flex-1 w-full bg-gray-100"
            />
          </div>
        </div>
      )}
    </section>
  );
}
